import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { Paper, Typography, Tooltip, IconButton } from '@material-ui/core';
import { Refresh } from '@material-ui/icons';
import { BackButton, hasAtLeastOnePermission } from '../../sc-utilities';
import { Delete } from '@material-ui/icons';
import MaterialTable from 'material-table';
import { hasPermission, localization, getDimensionsOfPagination } from '../../sc-utilities';
import ExternalSystemSettingDelete from './ExternalSystemSettingDelete';
import * as actions from './store/externalSystemSettingActions';
import * as action from '../../np-core/Auth/store/authActions';
import Toolbar from '../../sc-utilities/Toolbar';
import { MuiTableRows } from '../../../themes';
import { CancelToken } from 'axios';
import ButtonGroup from '../../sc-utilities/ButtonGroup';

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    typography: {
        marginRight: '1%',
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const ExternalSystemSettingList = props => {
    const { classes, translations, permissions, onFetchExternalSystemSettings, onFetchExternalSystemSettingsCount, fetching, saveColumnsFilter, activeCustomerID, onBulkDeleteExternalSystemSetting, tableColumns, redirectHandler, deleting, tableRef } = props;
    const [deleteModalState, setDeleteModalState] = useState({ open: false, externalSystemSettingIDs: [] });
    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []); const countSource = CancelToken.source();
    const listSource = CancelToken.source();

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: hasAtLeastOnePermission(permissions, ['external_system_setting_delete']),
        selectionProps: rowData => {
            return { id: `checkbox_${rowData.ExternalSystemSettingID}` }
        },
        search: false,
        minBodyHeight: window.innerHeight - 197 < 300 ? 300 : window.innerHeight - 197,
        maxBodyHeight: window.innerHeight - 197,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        exportFileName: 'External System Settings',
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    useEffect(() => {
        return () => {
            countSource.cancel('Operation canceled by the user.');
            listSource.cancel('Operation canceled by the user.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    }

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);

        query['ActiveCustomerID'] = activeCustomerID;
        query['q'] = q;
        let count = await onFetchExternalSystemSettingsCount(query, { countSource: countSource });
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count.data)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        } let result = count.data ? await onFetchExternalSystemSettings(query, { listSource: listSource }) : { data: [] };
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result.data, page: query.page, pageSize: pageSize, totalCount: count.data };
    }
    //  redirectHandler(`/admin/deviceDependentSettingsCreate`)
    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography className={classes.typography}>{translations['external_system_settings'] || 'External system settings'}</Typography>
                {hasPermission(permissions, 'external_system_setting_create') ?

                    <ButtonGroup
                        options={[
                            { label: translations['service_setting'] || 'Service Setting', value: 'service' },
                            { label: translations['database_setting'] || 'Database Setting', value: 'database' },
                            { label: translations['json_setting'] || 'JSON Setting', value: 'json' },
                            { label: translations['kloudless_setting'] || 'Kloudless Setting', value: 'kloudless' },
                            { label: translations['servicenow_setting'] || 'ServiceNow Setting', value: 'servicenow' },
                            { label: translations['makor_setting'] || 'Makor ERP Setting', value: 'makor' },
                            { label: translations['store_external_data_setting'] || 'Store External Data Setting', value: 'store_external_data' }]}
                        onClick={(data) => redirectHandler(`/externalSystemSetting/create?type=${data}`)}
                    >
                    </ButtonGroup>


                    : null}
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" disabled={fetching} onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    }

    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, externalSystemSettingIDs: [] });
    }

    const handleCloseDanger = async () => {
        await onBulkDeleteExternalSystemSetting({ ExternalSystemSettingIDs: deleteModalState.externalSystemSettingIDs, ActiveCustomerID: activeCustomerID });
        setDeleteModalState({ open: false, externalSystemSettingIDs: [] });
        tableRef.current && tableRef.current.onQueryChange()
    };

    const handleBulkDelete = externalSystemSettingIDs => {
        setDeleteModalState({ open: true, externalSystemSettingIDs: externalSystemSettingIDs });
    };

    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["external_system_settings"] || "External system settings"}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(externalSysytemSetting => externalSysytemSetting.ExternalSystemSettingID))
        }
    ]

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[`ExternalSystemSettings`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID })
        return newColumnState
    }

    return (
        <Paper>
            <MaterialTable
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={fetching}
                localization={localization(translations)}
                onChangeRowsPerPage={(size) => {
                    setTableOptions({ ...tableOptions, pageSize: size })
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {hasPermission(permissions, 'external_system_setting_delete') ? (
                <ExternalSystemSettingDelete
                    entityName={translations['external_system_settings'] || 'External system settings'}
                    translations={translations}
                    handleCloseDanger={handleCloseDanger}
                    handleCloseSafe={handleCloseSafe}
                    deleteModalState={deleteModalState}
                    deleting={deleting}
                />
            ) : null}
        </Paper>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || [],
        fetching: state.externalSystemSettings.externalSystemSetting.fetching,
        deleting: state.externalSystemSettings.externalSystemSetting.deleting
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchExternalSystemSettingsCount: (filter, options) => dispatch(actions.fetchExternalSystemSettingCount(filter, options)),
        onFetchExternalSystemSettings: (filter, options) => dispatch(actions.fetchExternalSystemSetting(filter, options)),
        onBulkDeleteExternalSystemSetting: setting => dispatch(actions.bulkDeleteExternalSystemSetting(setting)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(ExternalSystemSettingList));